import { useQuery } from '@tanstack/react-query';
import { ApiError } from '@/lib/http';
import { storefrontApi } from '../api/client';
import { storefrontKeys } from '../api/keys';
import type {
  StorefrontOrderDetail,
  StorefrontOrdersResponse,
} from '../api/types';

/**
 * Os pedidos do buyer.
 *
 * `staleTime` curto de propósito: esta é a tela de "onde está meu pedido", e
 * quem a abre pela segunda vez está checando se mudou. Um cache longo
 * responderia com o degrau de dez minutos atrás — exatamente a pergunta que
 * trouxe a pessoa aqui.
 *
 * Quem nunca comprou recebe 200 com as duas listas vazias, não 404: "ainda não
 * fiz pedido" é estado normal, e um erro obrigaria a tela a um ramo especial
 * para a primeira visita.
 */
export function useOrders() {
  return useQuery<StorefrontOrdersResponse, ApiError>({
    queryKey: storefrontKeys.orders(),
    queryFn: () => storefrontApi.orders(),
    staleTime: 30_000,
  });
}

/**
 * Um pedido, com as peças.
 *
 * Sem `retry` no 404: pedido de outra pessoa responde igual a pedido
 * inexistente, e insistir três vezes só atrasaria a tela de "não encontrado".
 */
export function useOrder(orderId: string | undefined) {
  return useQuery<StorefrontOrderDetail, ApiError>({
    queryKey: storefrontKeys.order(orderId ?? ''),
    queryFn: () => storefrontApi.order(orderId as string),
    enabled: Boolean(orderId),
    staleTime: 30_000,
    retry: (falhas, erro) => erro.status !== 404 && falhas < 2,
  });
}
